import { DoubleArrow } from '@mui/icons-material';
import { Box, Dialog, DialogContent, Grid, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie';
import { LoadingIndicator } from '../../../../Components/CoreLib/library';
import { ExpandableSectionHandle } from '../../../../Components/ExpandableSection';
import { ExpandableSection } from '../../../../Components/ExpandableSection/ExpandableSection';
import { StepDataDto, StepDataType, StepDto } from '../../../../dtos';
import { useDragStatus } from '../../../../Hooks';
import { NodeTypeFactory, NodeTypes } from '../../NodeTypes/utils';
import { useStepForm } from '../../Utils';
import { StepEditorContext } from '../../Utils/StepEditorContext';
import { ModalSaveCloseFooter, ModalTitle } from '../SubComponents';
import { SmartValueList, SmartValueListItem, SmartValueSlice, SmartValueSource } from './EditStepModalForms/StepFormParts';
import { useGetJobFunctionNamesQuery } from '../../../../store/generated/generatedApi';

export interface IEditStepModalProps {
    step?: StepDto;
    collectedData?: StepDataDto[];
    updateStep: (updatedStep: StepDto) => void;
    clearSelectedStep: () => void;
}

export const EditStepModal: FC<IEditStepModalProps> = (props) => {
    const { step, updateStep, clearSelectedStep, collectedData = [] } = props;
    const isDragging = useDragStatus();
    const cookies = useMemo(() => new Cookies(null), []);
    const isCollectedDataPanelOpenInRoute = cookies.get('cpd');
    const isNullOrOne = (val: string | null) => val === null || val === '1';
    const [isCollectedDataPanelExpanded, setIsCollectedDataPanelExpanded] = useState(isNullOrOne(isCollectedDataPanelOpenInRoute));
    const { data: jobFunctionNames } = useGetJobFunctionNamesQuery();

    useEffect(() => {
        cookies.set('cpd', isCollectedDataPanelExpanded ? '1' : '0');
    }, [isCollectedDataPanelExpanded, cookies]);

    const stepForm = useStepForm(
        {
            save: updateStep,
            cancel: clearSelectedStep,
            initValues: step,
            isLoading: false,
        },
        step?.key
    );

    const smartValueSections = useMemo(() => {
        var sections: SmartValueList[] = [];
        // add collected data first because it should show up at the top of the lists
        sections.push({
            name: 'Previous Steps',
            items: collectedData.filter(cd => !!cd.name).map<SmartValueListItem>(cd => ({ displayName: cd.name!, value: cd.name!, metadata: { source: SmartValueSource.PREVIOUS_STEP, type: cd.dataType ?? StepDataType.TEXT, isRequired: cd.isRequired, isFutureDate: cd.isFutureDate } })),
        });

        // if the step is an assignable type then we should add the assigned user data
        if (step?.type === NodeTypes.STEP) {
            sections.push({
                name: 'Assigned User',
                items: [
                    ...(stepForm.formStep.isExternal ? [] : [
                        { displayName: "First Name", value: "Assigned User's First Name", metadata: { source: SmartValueSource.CURRENT_STEP, type: StepDataType.TEXT, isRequired: true} },
                        { displayName: "Last Name", value: "Assigned User's Last Name", metadata: { source: SmartValueSource.CURRENT_STEP, type: StepDataType.TEXT, isRequired: true} },
                    ]),
                    { displayName: "Email", value: "Assigned User's Email", metadata: { source: SmartValueSource.CURRENT_STEP, type: StepDataType.TEXT, isRequired: true} },
                ],
            });
        }

        sections.push({
            name: 'Play Starter',
            items: [
                { displayName: "First Name", value: "Play Starter's First Name", metadata: { source: SmartValueSource.PLAY, type: StepDataType.TEXT, isRequired: true } },
                { displayName: "Last Name", value: "Play Starter's Last Name", metadata: { source: SmartValueSource.PLAY, type: StepDataType.TEXT, isRequired: true } },
                { displayName: "Email", value: "Play Starter's Email", metadata: { source: SmartValueSource.PLAY, type: StepDataType.TEXT, isRequired: true } },
            ],
        });

        if (jobFunctionNames) {
            jobFunctionNames.forEach(jobFunctionName => {
                sections.push({
                    name: jobFunctionName,
                    items: [
                        { displayName: "First Name", value: `${jobFunctionName} Job Function First Name`, metadata: { source: SmartValueSource.JOB_FUNCTION, type: StepDataType.TEXT, isRequired: true } },
                        { displayName: "Last Name", value: `${jobFunctionName} Job Function Last Name`, metadata: { source: SmartValueSource.JOB_FUNCTION, type: StepDataType.TEXT, isRequired: true } },
                        { displayName: "Email", value: `${jobFunctionName} Job Function Email`, metadata: { source: SmartValueSource.JOB_FUNCTION, type: StepDataType.TEXT, isRequired: true } },
                    ],
                });
            });
        }

        return sections;
    }, [collectedData, step?.type, stepForm.formStep.isExternal, jobFunctionNames]);

    const allSmartValues = useMemo(() => smartValueSections.flatMap(s => s.items), [smartValueSections]);

    const renderFormLayout = useMemo(() => {
        if (!step) {
            return <LoadingIndicator />;
        }

        var nodeTypeFactory = new NodeTypeFactory();
        var nodeType = nodeTypeFactory.getNodeTypeForStepType(step.type);
        if (nodeType.isEditable) {
            const EditModalContent = nodeType.EditModalContent!;
            return <EditModalContent key={step.key} step={step} smartValues={allSmartValues} />;
        }

        return (
            <Grid item>
                <Typography>There are no editable fields on this step. Click the Close/Cancel button below to close this modal.</Typography>
            </Grid>
        );
    }, [step, allSmartValues]);

    return (
        <Dialog
            PaperProps={{
                sx: {
                    height: '80vh',
                    backgroundColor: isDragging ? '#787878' : undefined,
                },
            }}
            open={!!step}
            fullWidth
            maxWidth='lg'>
            <DialogContent sx={{ padding: 0 }}>
                <Box flexDirection='column' display='flex' height='100%'>
                    <ModalTitle icon={<DoubleArrow sx={{ mr: 1 }} />} title='Edit Step' />
                    <Box flexDirection='row' display='flex' overflow='hidden' justifyContent='start' alignItems='stretch' height='100%'>
                        <Box height='100%'>
                            {step?.type !== NodeTypes.START && (
                                <ExpandableSection isExpanded={isCollectedDataPanelExpanded} direction='right'>
                                    <SmartValueSlice smartValueSections={smartValueSections} />
                                </ExpandableSection>
                            )}
                        </Box>
                        <Box flexGrow={1} display='flex' flexDirection='column' justifyContent='space-between' position='relative'>
                            {step?.type !== NodeTypes.START && (
                                <ExpandableSectionHandle
                                    isExpanded={isCollectedDataPanelExpanded}
                                    setIsExpanded={setIsCollectedDataPanelExpanded}
                                    direction='right'
                                    backgroundColor='#f3f5f6'
                                />
                            )}
                            <Box overflow='auto' sx={{ height: '100%' }} className={isDragging ? 'draggable-input-area' : undefined}>
                                <StepEditorContext.Provider value={stepForm}>{renderFormLayout}</StepEditorContext.Provider>
                            </Box>
                            <Box px={1} sx={{ borderTop: '1px solid #e4e4e4', background: 'white' }}>
                                <ModalSaveCloseFooter
                                    onCancelCloseClicked={clearSelectedStep}
                                    isFormDirty={stepForm.isFormDirty}
                                    onSaveClicked={stepForm.handleSave}
                                    onDeleteClicked={stepForm.handleDelete}
                                    hideDeleteButton={step?.type === NodeTypes.START}
                                    deletePermissions={[`delete:steps`]}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
